import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Building, Users, Award, Globe } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Center = () => {
  const { language } = useLanguage();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const response = await axios.get(`${API}/team`);
        setTeamMembers(response.data);
      } catch (error) {
        console.error('Error fetching team members:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTeamMembers();
  }, []);

  return (
    <div className="min-h-screen" data-testid="center-page">
      {/* Hero */}
      <section className="relative bg-gradient-to-r from-green-600 to-teal-500 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Building className="w-16 h-16 mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {language === 'de' ? 'Unser Center & Team' : 'Our Center & Team'}
          </h1>
          <p className="text-xl text-green-100">
            {language === 'de' 
              ? 'Professionelle Ausbildung und erstklassiger Service' 
              : 'Professional training and first-class service'}
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              {language === 'de' 
                ? 'An unserer Kitesurf und Windsurf Station findest du unsere geschulten und hilfsbereiten Mitarbeiter' 
                : 'At our kitesurf and windsurf station you will find our trained and helpful staff'}
            </h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed">
              {language === 'de'
                ? 'Unser Team besteht aus super Leuten die ein und dieselbe Vision teilen: wir wollen die Besten sein in dem was wir tun. Wir sind ambitioniert, leidenschaftlich und ehrgeizig.'
                : 'Our team consists of great people who share one and the same vision: we want to be the best at what we do. We are ambitious, passionate and determined.'}
            </p>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-gradient-to-br from-green-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Users className="w-16 h-16 mx-auto mb-4 text-green-600" />
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {language === 'de' ? 'Lerne unser Team kennen' : 'Meet Our Team'}
            </h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed mb-6">
              {language === 'de'
                ? 'Unser Team besteht aus super Leuten die ein und dieselbe Vision teilen: wir wollen die Besten sein in dem was wir tun. Wir sind ambitioniert, leidenschaftlich und ehrgeizig. Wir verfolgen alle ein gemeinsames Ziel, nämlich unsere Kunden glücklich zu machen. Unser Team ist eine grosse Familie, die jeden Tag für unsere Gäste versucht unvergesslich zu machen und wird dafür sorgen das es einfach für euch wird zu sagen.'
                : 'Our team consists of great people who share one and the same vision: we want to be the best at what we do. We are ambitious, passionate and determined. We all pursue a common goal, namely to make our customers happy. Our team is a large family that tries to make every day unforgettable for our guests and will make it easy for you to say.'}
            </p>
            <p className="text-2xl font-bold text-green-600 italic mb-8">
              {language === 'de' ? '" WIR KOMMEN WIEDER "' : '" WE WILL BE BACK "'}
            </p>
          </div>

          {/* Team Members Grid */}
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-600">
                {language === 'de' ? 'Team-Mitglieder werden geladen...' : 'Loading team members...'}
              </p>
            </div>
          ) : teamMembers.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {teamMembers.map((member) => (
                <div 
                  key={member.id} 
                  className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={`${BACKEND_URL}/api${member.photo_url}`}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{member.name}</h3>
                    <p className="text-sm text-green-600 font-semibold mb-2">
                      {language === 'de' ? member.position_de : member.position_en}
                    </p>
                    <div className="flex items-center text-sm text-gray-600">
                      <Globe className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span className="line-clamp-2">{member.languages}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              {language === 'de' 
                ? 'Unser Team wird bald vorgestellt. Schauen Sie bald wieder vorbei!' 
                : 'Our team will be introduced soon. Check back soon!'}
            </div>
          )}
        </div>
      </section>

      {/* Professional Training */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Award className="w-16 h-16 mx-auto mb-4 text-green-600" />
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              {language === 'de' 
                ? 'Professionelle Ausbildung & Zertifizierungen' 
                : 'Professional Training & Certifications'}
            </h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed">
              {language === 'de'
                ? 'Ein Kitesurf und Windsurf Spot lebt von seinem Team. Wir bei Surfmotion achten darauf, professionelle Mitarbeiter zur Verfügung zu stellen. VDWS- oder IKO lizenzierte Kitesurf- und Windsurf-Trainer stehen für Top-Schulung zur Verfügung, um euch einen wunderbaren Kitesurf- oder Windsurf-Urlaub in der SomaBay möglich zu machen.'
                : 'A kitesurf and windsurf spot lives from its team. At Surfmotion we make sure to provide professional staff. VDWS or IKO licensed kitesurf and windsurf instructors are available for top training to make a wonderful kitesurf or windsurf vacation in Soma Bay possible for you.'}
            </p>
          </div>

          {/* Certifications */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-green-100 to-teal-100 rounded-lg p-8 text-center">
              <div className="bg-white w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">VDWS</h3>
              <p className="text-gray-700">
                {language === 'de' 
                  ? 'Lizenzierte Trainer nach VDWS-Standard' 
                  : 'Licensed instructors according to VDWS standard'}
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-100 to-green-100 rounded-lg p-8 text-center">
              <div className="bg-white w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-10 h-10 text-teal-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">IKO</h3>
              <p className="text-gray-700">
                {language === 'de' 
                  ? 'Zertifizierte Instruktoren nach IKO-Standard' 
                  : 'Certified instructors according to IKO standard'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-teal-500 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            {language === 'de' 
              ? 'Bereit für dein Abenteuer?' 
              : 'Ready for your adventure?'}
          </h2>
          <p className="text-xl text-green-100 mb-8">
            {language === 'de'
              ? 'Buche jetzt deinen Kurs und werde Teil unserer Surfmotion Familie!'
              : 'Book your course now and become part of our Surfmotion family!'}
          </p>
          <a
            href="https://reservations.surfmotion.com/reservation"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-white text-green-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            {language === 'de' ? 'Vorab-Registrierung' : 'Pre-Registration'}
          </a>
        </div>
      </section>
    </div>
  );
};

export default Center;